import { pdsTv } from '@purinanbm/pds-ui';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React from 'react';
import { Link } from 'src/components/Link';
import { themeVariant } from 'src/theme/basic-styles';
import { CMS_THEME } from '../../common/enums';
import SubnavMenuDesktop from './SubnavMenuDesktop';
import SubnavMenuMobile from './SubnavMenuMobile';

const styles = pdsTv({
  slots: {
    contentContainer:
      'pds-height-full pds-z-10 pds-flex pds-h-[68px] pds-w-full pds-items-center pds-justify-between pds-gap-4 pds-px-4 md:pds-px-4.5 lg:pds-px-5.5',
    textContainer: 'pds-m-0 pds-text-title-md pds-font-extrabold md:pds-min-w-[378px]',
  },
  variants: {
    theme: {
      light: {
        contentContainer: 'pds-bg-surface',
        textContainer: themeVariant({ isOnlyText: true }).light,
      },
      dark: {
        contentContainer: 'pds-bg-neutral',
        textContainer: themeVariant({ isOnlyText: true }).dark,
      },
      neutral: {
        contentContainer: '',
        textContainer: themeVariant({ isOnlyText: true }).light,
      },
    },
  },
});

type Props = {
  node: IStorageSubnavMenu;
};

const defaultSettings = {
  entity_variant: {
    theme: CMS_THEME.DARK,
  },
};

const StorageSubnavMenu = ({ node: subnav }: Props) => {
  const settings = Object.assign(defaultSettings, subnav?.behaviors || {});
  const breakpoint = useBreakpoint();
  const { links } = subnav;
  const list = React.useMemo(
    () =>
      links.map(link => ({
        id: link.title,
        label: link.title,
        href: link.url,
      })),
    [links],
  );

  const [isInitialized, setIsInitialized] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    setIsDesktop(breakpoint.md);
    setIsInitialized(true);
  }, [breakpoint, list, subnav.links]);

  const cmsTheme = settings.entity_variant.theme;

  const { contentContainer, textContainer } = React.useMemo(
    () => styles({ theme: cmsTheme }),
    [cmsTheme],
  );

  const handleGA = (title: string) => {
    sendIt({
      event: 'nav_click',
      eventParams: {
        nav_item: title,
        nav_type: 'brand_sub_navigation',
      },
    });
  };

  if (list.some(item => !item.href) || !subnav.title) {
    return null;
  }

  if (!isInitialized) {
    return (
      <nav
        id={subnav.component_id}
        data-id={subnav.drupal_id}
        className="pds-relative"
        aria-busy="true"
        aria-label="Navigation menu loading"
      >
        <div className={contentContainer()}>
          <div className={textContainer()}>
            <span className={textContainer()}>{subnav.title}</span>
          </div>

          <div className="pds-flex pds-items-center">
            <ul className="pds-sr-only">
              {list.map(item => (
                <li key={item.id}>
                  <Link partiallyActive to={item.href}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="pds-flex pds-items-center pds-gap-4" aria-hidden="true">
              {[...Array(Math.min(5, list.length))].map((_, i) => (
                <div
                  key={i}
                  className="pds-h-4 pds-w-5 pds-animate-pulse pds-rounded pds-bg-gray-200"
                />
              ))}
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return isDesktop ? (
    <nav
      id={subnav.component_id}
      aria-label={subnav?.title}
      data-id={subnav.drupal_id}
      className="pds-relative"
    >
      <div className={contentContainer()}>
        <div className={textContainer()}>
          <span className={textContainer()}>{subnav.title}</span>
        </div>
        <SubnavMenuDesktop items={list} handleGA={handleGA} theme={cmsTheme} />
      </div>
    </nav>
  ) : (
    <SubnavMenuMobile
      items={list}
      theme={cmsTheme}
      handleGA={handleGA}
      title={subnav?.title ?? ''}
    />
  );
};

export default StorageSubnavMenu;
